import { Box } from "@mui/material";
import React, { ForwardedRef } from "react";
import {
  ConnectDragPreview,
  ConnectDragSource,
  DragSourceMonitor,
  useDrag,
  XYCoord,
} from "react-dnd";

export interface DraggableProps<T = any> {
  type?: string;
  item: T;
  onDrag?: (
    isDragging: boolean,
    item: T,
    monitor: DragSourceMonitor<T>
  ) => void;
  canDrag?: boolean;
  isDragging?: (monitor: DragSourceMonitor<T>) => boolean;
}

export interface PreviewProps<T = any> {
  item?: T;
}

const Draggable = <T,>(props: React.PropsWithChildren<DraggableProps<T>>) => {
  const { dragRef } = useDraggable(props);

  const handleDragRef = useDragRef(dragRef);

  return (
    <Box ref={handleDragRef} sx={{ cursor: "grab" }}>
      {props.children}
    </Box>
  );
};

type CollectType = {
  isDragging: boolean;
  offset: XYCoord | null;
};
export const useDraggable = <T,>(
  props: DraggableProps<T>
): {
  collect: CollectType;
  dragRef: ConnectDragSource;
  dragPreview: ConnectDragPreview;
} => {
  const { type = "*", item, onDrag, canDrag = true, isDragging } = props;

  const hasDragEvents = typeof onDrag !== "undefined";
  const [collect, dragRef, dragPreview] = useDrag({
    type: type,
    item: hasDragEvents
      ? (monitor) => {
          onDrag(true, item, monitor);
          return item;
        }
      : item,
    end: (item, monitor) => {
      if (hasDragEvents) onDrag(false, item, monitor);
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      offset: monitor.getDifferenceFromInitialOffset(),
    }),
    canDrag: (monitor) => canDrag,
    isDragging,
  });
  return { collect, dragRef, dragPreview };
};

export const useDragRef = (dragRef: ConnectDragSource) => {
  return React.useCallback(
    (node: HTMLElement | null) => {
      if (node) dragRef(node);
    },
    [dragRef]
  );
};

export default Draggable;
